import React, { FormEventHandler, useCallback, useEffect, useRef } from 'react'
import TextInput from 'components/Common/Form/Input/TextInput'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { formToObject } from 'lib/forms/formToObject'
import { register } from 'actions/AuthActions'
import TextButton from 'components/Luxkit/Button/TextButton'
import SquareCheckboxInput from 'components/Luxkit/Checkbox/SquareCheckboxInput'
import BodyText from 'components/Luxkit/Typography/BodyText'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import PhoneInput from 'components/Common/Form/Input/PhoneInput'
import LegalText from './AccountAccessLegalText'
import config from 'constants/config'
import Group from 'components/utils/Group'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import noop from 'lib/function/noop'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { isLoggedIn } from 'selectors/accountSelectors'
import { AccountAccessModalResult } from './AccountAccessModal'
import * as Analytics from 'analytics/analytics'
import { clientEvent } from 'analytics/snowplow/events'

interface PhoneNum {
  phone: string;
  prefix: string;
}

interface JoinForm {
  email: string;
  password: string;
  givenName: string;
  firstName: string;
  phoneNum?: PhoneNum;
  postcode: string;
}

interface Props {
  error?: any;
  regionCode?: string;
  email: string;
  onModeChange?: (mode: App.UiAccountModalMode) => void;
}

function AccountAccessJoin(props: Props) {
  const {
    regionCode,
    error,
    email,
    onModeChange = noop,
  } = props

  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const firstInput = useRef<HTMLInputElement>(null)
  const loggedIn = useAppSelector(isLoggedIn)

  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  useEffect(() => {
    Analytics.track('client-event', clientEvent('sign_up_details', 'impression', 'logging', 'operational'))
    firstInput.current?.focus()
  }, [])

  const termsCheckboxRef = useRef<HTMLInputElement>(null)

  const dispatch = useAppDispatch()

  const onJoin = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    const joinData = formToObject<JoinForm>(event.currentTarget)
    const phoneNum = joinData.phoneNum
    delete joinData.phoneNum

    dispatch(register({
      ...joinData,
      ...(phoneNum?.phone && {
        phone: phoneNum.phone,
        phonePrefix: phoneNum.prefix,
      }),
    }, 'email'))
  }, [dispatch])

  const onBack = useCallback(() => onModeChange('login'), [onModeChange])

  return (
    <>
      <ModalHeader
        title="Welcome"
        onBackButtonClick={onBack}
      />
      <ModalBody>
        <ModalContent>
          <form name="joinForm" onSubmit={onJoin}>
            <Group direction="vertical" gap={12}>
              <Group direction="vertical" gap={20}>
                {error?.message && <MessageBanner kind="critical" description={error.message} />}
                <BodyText variant="large">
                  Let’s create an account for <b>{email}</b>
                </BodyText>
                <Group
                  direction="vertical"
                  tabletDirection="horizontal"
                  tabletHorizontalAlign="stretch"
                  gap={12}
                >
                  <TextInput
                    id="givenName"
                    required
                    type="text"
                    name="givenName"
                    placeholder="First Name"
                    autoComplete="given-name"
                    requiredErrorMessage="First name is required"
                    ref={firstInput}
                    label="First name"
                    pattern="^[^\.\$\%\&\(\)\*!,@#\d]*$"
                    invalidErrorMessage="Please enter a valid first name"
                    style={{ width: 'auto' }}
                  />
                  <TextInput
                    id="surname"
                    required
                    type="text"
                    name="surname"
                    placeholder="Last Name"
                    autoComplete="family-name"
                    requiredErrorMessage="Last name is required"
                    label="Last name"
                    pattern="^[^\.\$\%\&\(\)\*!,@#\d]*$"
                    invalidErrorMessage="Please enter a valid last name"
                    style={{ width: 'auto' }}
                  />
                </Group>
              </Group>
              <HiddenInput
                id="email"
                name="email"
                value={email}
              />
              <PhoneInput
                id="phoneNum"
                name="phoneNum"
                label="Phone number"
                prefixCountry={regionCode}
              />
              {config.signUp.showPostcode && (
                <TextInput
                  id="postcode"
                  required
                  type="text"
                  name="postcode"
                  placeholder="Postcode"
                  autoComplete="off"
                  requiredErrorMessage="Postcode is required"
                  label="Postcode"
                />
              )}
              <TextInput
                id="password"
                required
                type="password"
                name="password"
                placeholder="Password"
                minLength={8}
                autoComplete="new-password"
                requiredErrorMessage="Password is required"
                label="Create password"
              />
              <SquareCheckboxInput
                ref={termsCheckboxRef}
                name="agreeTandC"
                data-testid="terms-and-privacy-agree-checkbox"
                required
              >
                <LegalText />
              </SquareCheckboxInput>
              <TextButton
                kind="primary"
                fit="flex"
                type="submit"
                size="large"
              >
                Create account
              </TextButton>
            </Group>
          </form>
        </ModalContent>
      </ModalBody>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    error: state.auth.error,
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(AccountAccessJoin)
